import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import Button from '@material-ui/core/Button';
import { onboardingTasks } from '@ourbranch/lookups';
import { IconButton, Menu, MenuItem } from '@material-ui/core';

import { Label } from 'core/components/label';
import { Tag } from 'core/components/tag';
import { capitalize, dateTimeFormatter } from 'core/helpers/formatters';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';
import viewTask from 'core/assets/svg/view-task.svg';
import infoIcon from 'core/assets/svg/info-icon-dark.svg';
import pendingIcon from 'core/assets/svg/clock-filled.svg';
import documentApprovalIcon from 'core/assets/svg/document-approval.svg';
import { DocumentModal } from 'core/components/document-upload-modal';

import undoIcon from 'customer/assets/undo.svg';
import uploadIcon from 'customer/assets/uploadIcon.svg';
import completedIcon from 'customer/assets/completed.svg';
import questionIcon from 'customer/assets/questionIcon.svg';
import { downloadTaskDocument } from 'customer/helpers/policy-document-service';
import { updateTaskData } from 'customer/helpers/task-mutations';

import ResetModal from '../reset-task/reset-task-modal';
import ApprovalModal from '../approve-task/approve-task-modal';
import MIPipOption4LearnMoreModal from '../mi-pip-option4-learn-more-modal/mi-pip-option4-learn-more-modal';

import useStyles from './task.styles';
const getDocName = (taskName, policyData) => {
  const { policyType, policyId } = policyData;
  const now = new Date();
  switch (taskName) {
    case onboardingTasks.SignApplication:
      return `${now.getFullYear()}-${
        now.getMonth() + 1
      }-${now.getDate()}-${now.getHours()}-${now.getMinutes()}-${policyType[0].toUpperCase()}_APPL`;
    case onboardingTasks.CompleteInventory:
      return `inventory_${policyId}`;
    case onboardingTasks.ConnectedHomeRenewal:
      return `connected_home_renewal_${policyId}`;
    case onboardingTasks.ConfirmWindstormMitigationStatus:
      return `proof_of_advanced_windstorm_mitigation_${policyId}`;
    case onboardingTasks.ProofOfMIPipOption4:
    case onboardingTasks.ProofOfMIPipOption5:
    case onboardingTasks.ProofOfMIPipOption6:
      return `proof_of_mi_pip_limit_eligibility_${policyId}`;
    default:
      return null;
  }
};

const getTaskDisplayName = (taskName) => {
  switch (taskName) {
    case onboardingTasks.NeedProofThatAccidentPreventionCourseTaken:
      return 'Proof of accident prevention certification';
    case onboardingTasks.NeedProofOfPriorCoverage:
      return 'Proof of prior auto insurance coverage';
    case onboardingTasks.ConfirmWindstormMitigationStatus:
      return 'Proof of advanced windstorm mitigation';
    case onboardingTasks.SignApplication:
      return 'Sign policy documents';
    case onboardingTasks.ConnectedHomeRenewal:
      return 'Proof of Connected Home for renewal';
    case onboardingTasks.ProvideProofOfConnectedHome:
      return 'Proof of Connected Home';
    case onboardingTasks.ProofOfMIPipOption4:
    case onboardingTasks.ProofOfMIPipOption5:
    case onboardingTasks.ProofOfMIPipOption6:
      return 'Provide proof of MI PIP medical limit eligibility';
    case onboardingTasks.ProvideProofOfFortifiedHome:
      return 'Proof of Fortified Home certification';
    default:
      return taskName;
  }
};

const appcuesModalIdsByTaskName = {
  [onboardingTasks.SignApplication]: '63249237-5e04-4437-a496-12ff76cd2648',
  [onboardingTasks.NeedProofOfPriorCoverage]: '5251e741-2a75-4e50-b245-5a1cef831dae',
  [onboardingTasks.ProvideProofOfConnectedHome]: '469fd3b0-415f-4b87-b355-8e9998f6a0ae',
  [onboardingTasks.ConnectedHomeRenewal]: '8417a4bd-f165-48c3-a01c-a845f2ff0029',
  [onboardingTasks.ProvideProofOfGoodStudent]: '3e9f15c2-1e9d-46d7-a0cd-bbbbfb300a0b',
  [onboardingTasks.NeedProofThatAccidentPreventionCourseTaken]: 'b3047d65-9347-4a65-bd77-70deb638bb1b',
  [onboardingTasks.ConfirmWindstormMitigationStatus]: '416d477c-d154-4e60-bd07-ead1f62a4582',
  [onboardingTasks.CompleteInventory]: '473c8d96-e71d-4ea5-83a6-ccf5078c96ea',
  [onboardingTasks.NeedScheduledPersonalPropertyImages]: '9570ff2c-f404-479f-b11c-72373cc01eb5',
  [onboardingTasks.ReviewPotentialUndisclosedDriver]: 'f5065e3e-ca48-41d4-82b1-1d900b0f2b37',
  [onboardingTasks.ProvideProofOfFortifiedHome]: 'a96c262f-26bf-489f-9ab3-2571cb7b7374',
  [onboardingTasks.ProofOfMIPipOption4]: '72c3bd49-ad5a-4721-9482-d1b88967e460',
  [onboardingTasks.ProofOfMIPipOption5]: '0f649d0a-7c88-4f4f-8896-e750a041236d',
  [onboardingTasks.ProofOfMIPipOption6]: '8c926e7b-771d-429f-b530-93d03dc639e9'
};

const processingReviewTasks = [
  onboardingTasks.NeedProofOfPriorCoverage,
  onboardingTasks.NeedProofThatAccidentPreventionCourseTaken,
  onboardingTasks.ConfirmWindstormMitigationStatus,
  onboardingTasks.ProvideProofOfConnectedHome,
  onboardingTasks.ConnectedHomeRenewal,
  onboardingTasks.ProofOfMIPipOption4,
  onboardingTasks.ProofOfMIPipOption5,
  onboardingTasks.ProofOfMIPipOption6,
  onboardingTasks.ProvideProofOfGoodStudent,
  onboardingTasks.SignApplication,
  onboardingTasks.ProvideProofOfFortifiedHome
];

const Task = ({
  taskId,
  taskName,
  data,
  completed,
  completedDate,
  policyId,
  readOnly,
  completedBy,
  rejections,
  approvalDate,
  approvedBy,
  onTaskApproved
}) => {
  const classes = useStyles();
  const {
    account: { id, completeTask, resetTask, approveTask, fetchFullAccount }
  } = useStore();
  const { canResetOnboardingTasks, isAgency } = useSession();
  const toast = useToast();
  const { customer, documentType, files, documentUrl: singleDocUrl } = data || {};
  const driver = data?.driver ? `${data.driver.firstName} ${data.driver.lastName}` : customer;
  const documentUrl = singleDocUrl ? singleDocUrl : files?.length === 1 ? files?.[0]?.documentUrl : null;

  const [showResetModal, setShowResetModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showMIPipOption4Modal, setShowMIPipOption4Modal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [, type] = policyId.split('-');
  const autoApproved = approvedBy === 'Auto Approved' || (approvalDate && approvalDate === completedDate);

  const handleReset = async () => {
    const res = await resetTask({
      accountId: id,
      taskId,
      policyId,
      taskName,
      completedBy,
      rejections
    });
    if (res.ok) {
      toast.notify({
        type: 'success',
        message: `The "${taskName}" task has been reset. `
      });
    } else {
      toast.notify({
        type: 'error',
        message: `There was an error resetting the task - ${res.error}`
      });
    }
  };

  const handleApprove = async () => {
    const res = await approveTask({
      taskId,
      accountId: id,
      policyId
    });
    if (res.ok) {
      toast.notify({
        type: 'success',
        message: `The "${taskName}" task has been approved.`
      });
      // Call the callback to keep the submitted section open
      if (onTaskApproved) {
        onTaskApproved();
      }
    } else {
      toast.notify({
        type: 'error',
        message: `There was an error approving the task - ${res.error}`
      });
    }
  };

  const showResetTaskIcon =
    canResetOnboardingTasks &&
    !(taskName === onboardingTasks.CompleteInventory) &&
    !(completedBy === 'member' && taskName === onboardingTasks.SignApplication);

  const handleViewFile = useCallback(
    (event) => {
      if (documentUrl) {
        downloadTaskDocument(documentUrl);
      } else if (files?.length > 1) {
        setAnchorEl(event.currentTarget);
      }
    },
    [documentUrl, files]
  );

  const handleCloseFileMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Get Appcues modal ID - Option 4 uses custom modal with dynamic data
  const isOption4 = taskName === onboardingTasks.ProofOfMIPipOption4;
  const appcuesModalId = appcuesModalIdsByTaskName[taskName];
  const hasLearnMore = isOption4 || appcuesModalId;
  const appCuesOnClick = () => {
    window?.Appcues?.show(appcuesModalId);
  };

  return (
    <div className={classNames(classes.container, { [classes.completed]: completed })}>
      <div className={classes.task}>
        {completed &&
          (approvalDate ? (
            <img alt="rounded checkmark" className={classes.completedIcon} src={completedIcon} />
          ) : (
            <img alt="review pending" src={pendingIcon} />
          ))}
        <div>
          <div className={classNames(classes.taskInfo, { [classes.taskInfoCompleted]: completed })}>
            <div className={classes.taskNameContainer}>
              <Label type="infoValue" className={classNames(classes.taskName, { [classes.completedTask]: completed })}>
                {getTaskDisplayName(taskName)}
              </Label>
              {hasLearnMore && (
                <IconButton
                  className={classes.learnMoreLink}
                  onClick={() => {
                    if (isOption4) {
                      setShowMIPipOption4Modal(true);
                    } else {
                      window?.Appcues?.show(appcuesModalId);
                    }
                  }}
                >
                  <div className={classes.learnMoreLinkText}>Learn More</div>
                  <img src={arrowIcon} height={14} width={14} alt="Learn More" />
                </IconButton>
              )}
            </div>
            {completedDate && (
              <Label variant="darkGreenSmall" className={classes.completedAt}>
                Submitted on {dateTimeFormatter(completedDate)} EST by {completedBy}
              </Label>
            )}
          </div>
          <div className={classes.tags}>
            <Tag className={classes.tag}>
              {capitalize(type)}
              {driver ? ` - ${driver}` : ''}
            </Tag>
            {completedDate && (
              <Tag
                className={classNames(classes.tag, classes.statusTag)}
                id={approvalDate ? 'task_approved_badge' : 'task_review_pending_badge'}
              >
                <img alt="status icon" src={approvalDate ? completedIcon : pendingIcon} />
                {approvalDate ? (autoApproved ? 'Auto-approved' : 'Approved') : 'Review pending'}
                {((approvalDate && autoApproved) || !approvalDate) && (
                  <IconButtonWithTooltip
                    icon={questionIcon}
                    iconClassName={classes.questionIcon}
                    tooltip={
                      approvalDate && autoApproved
                        ? "Because of the task type or details provided, this task is auto-approved and doesn't need Branch staff to review it."
                        : 'Please allow up to 3 business days for this task to be reviewed.'
                    }
                  />
                )}
              </Tag>
            )}
          </div>
        </div>
      </div>
      {completed ? (
        <div className={classes.task}>
          <div className={classes.iconButtons}>
            {showResetTaskIcon && (
              <Button onClick={() => setShowResetModal(true)} className={classes.actionButton}>
                <img src={undoIcon} alt="undo" className={classes.actionButtonIcon} />
                Reset
              </Button>
            )}
            {(documentUrl || files?.length) && (
              <Button onClick={handleViewFile} className={classes.actionButton}>
                <img src={viewTask} alt="view file" className={classes.actionButtonIcon} />
                {files?.length > 1 ? `View ${files.length} files` : 'View file'}
              </Button>
            )}
            {files && files?.length > 1 && (
              <Menu
                id="tooltip-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseFileMenu}
              >
                {files.map((f, index) => {
                  const fullFileName =
                    typeof f.documentUrl === 'string' ? f.documentUrl.split('/').pop() : f.fileName || 'Unknown file';
                  const displayName = fullFileName
                    ? fullFileName.replace(`${taskName} - `, '').replace(`${taskName}-`, '')
                    : 'Unknown file';

                  return (
                    <MenuItem
                      key={index}
                      onClick={() => downloadTaskDocument(f.documentUrl)}
                      className={classes.fileMenuItem}
                    >
                      {displayName}
                    </MenuItem>
                  );
                })}
                <MenuItem
                  key="view-all"
                  onClick={() => {
                    files.forEach((f) => downloadTaskDocument(f.documentUrl));
                    handleCloseFileMenu();
                  }}
                >
                  View all documents
                </MenuItem>
              </Menu>
            )}
          </div>
        </div>
      ) : (
        <div>
          {!readOnly && (
            <Button
              onClick={() => {
                setShowUploadModal(true);
              }}
              disableFocusRipple
              className={classes.upload}
              variant="text"
              color="secondary"
            >
              <img alt="upload icon" className={classNames(classes.uploadIcon)} src={uploadIcon} />
              Upload and submit
            </Button>
          )}
        </div>
      )}
      {canResetOnboardingTasks && completed && !approvalDate && (
        <div className={classes.approvalSection}>
          <img src={infoIcon} alt="approval icon" />
          <div className={classNames(classes.column, classes.approvalContent)}>
            <Label type="body3" fontWeight={600}>
              This task requires your review.
            </Label>
            <Label type="body3">
              Review the documents and either approve or reset the task. Resetting requests new documentation from the
              member and, if applicable, their agent.
            </Label>
          </div>
          <div className={classes.column}>
            <Button onClick={() => setShowApproveModal(true)} className={classes.approvalButton}>
              <img src={documentApprovalIcon} alt="undo" />
              Approve
            </Button>
          </div>
        </div>
      )}

      <ResetModal
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        taskName={taskName}
        onReset={handleReset}
        completedBy={completedBy}
        rejections={rejections}
      />
      <ApprovalModal
        open={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        taskName={taskName}
        onApprove={handleApprove}
        completedBy={completedBy}
        rejections={rejections}
      />
      <MIPipOption4LearnMoreModal
        open={showMIPipOption4Modal}
        onClose={() => setShowMIPipOption4Modal(false)}
        data={data}
      />

      <DocumentModal
        open={showUploadModal}
        onClose={async () => {
          setShowUploadModal(false);
          await fetchFullAccount(id);
        }}
        documents={files || []}
        taskId={taskId}
        accountId={id}
        onUpdateTaskData={updateTaskData}
        completeTask={completeTask}
        taskName={taskName}
        policyId={policyId}
        data={data}
        driver={driver}
        documentType={documentType}
        isAgency={isAgency}
        processingReviewTasks={processingReviewTasks}
        appCuesOnClick={appCuesOnClick}
      />
    </div>
  );
};

Task.propTypes = {
  taskId: PropTypes.string.isRequired,
  taskName: PropTypes.string.isRequired,
  data: PropTypes.object,
  completed: PropTypes.bool.isRequired,
  completedDate: PropTypes.string,
  readOnly: PropTypes.bool,
  completedBy: PropTypes.string,
  approvalDate: PropTypes.string,
  approvedBy: PropTypes.string,
  rejections: PropTypes.array,
  policyId: PropTypes.string,
  onTaskApproved: PropTypes.func
};

Task.defaultProps = {
  data: {},
  completedDate: null,
  approvalDate: false,
  readOnly: false
};

export default observer(Task);
